/**
 * Demographics — Diagnosis & Nutrition (diet + restriction fields).
 * Shared between add patient, demographics edit/view, and print.
 */

export const DIET_TYPE_OPTIONS = [
  { value: "Regular", label: "Regular" },
  { value: "Mechanical Soft", label: "Mechanical Soft" },
  { value: "Soft / Chopped", label: "Soft / Chopped" },
  { value: "Pureed", label: "Pureed" },
  { value: "Liquid", label: "Liquid" },
  { value: "Clear Liquid", label: "Clear Liquid" },
  { value: "Full Liquid", label: "Full Liquid" },
  { value: "NPO", label: "NPO" },
  { value: "Diabetic", label: "Diabetic" },
  { value: "Cardiac / Low Sodium", label: "Cardiac / Low Sodium" },
  { value: "Renal", label: "Renal" },
  { value: "High Protein / Calorie", label: "High Protein / Calorie" },
  { value: "Gluten Free", label: "Gluten Free" },
  { value: "Lactose Free", label: "Lactose Free" },
  { value: "Kosher", label: "Kosher" },
  { value: "Halal", label: "Halal" },
  { value: "Other", label: "Other" },
];

export const FOOD_TEXTURE_OPTIONS = [
  { value: "Regular", label: "Regular" },
  { value: "Easy to Chew", label: "Easy to Chew" },
  { value: "Soft", label: "Soft" },
  { value: "Minced & Moist", label: "Minced & Moist" },
  { value: "Pureed", label: "Pureed" },
  { value: "Liquidized", label: "Liquidized" },
  { value: "Not Applicable", label: "Not Applicable" },
];

export const FLUID_MANAGEMENT_OPTIONS = [
  { value: "No Restriction", label: "No Restriction" },
  { value: "Encourage Fluids", label: "Encourage Fluids" },
  { value: "Restrict Fluids", label: "Restrict Fluids" },
];

export const MEAL_ASSISTANCE_OPTIONS = [
  { value: "Independent", label: "Independent" },
  { value: "Setup Only", label: "Setup Only" },
  { value: "Supervision", label: "Supervision" },
  { value: "Partial Assist", label: "Partial Assist" },
  { value: "Total Assist / Feeding", label: "Total Assist / Feeding" },
];

export const SUPPLEMENT_ENTERAL_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Oral Supplement", label: "Oral Supplement" },
  { value: "Tube Feeding", label: "Tube Feeding" },
  { value: "TPN", label: "TPN" },
  { value: "Other", label: "Other" },
];

export const SODIUM_RESTRICTION_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Low Sodium (2g)", label: "Low Sodium (2g)" },
  { value: "Moderate (3g)", label: "Moderate (3g)" },
  { value: "Strict (<2g)", label: "Strict (<2g)" },
  { value: "Physician Ordered", label: "Physician Ordered" },
  { value: "Other", label: "Other" },
];

export const CARBOHYDRATE_RESTRICTION_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Diabetic Diet", label: "Diabetic Diet" },
  { value: "Carb Controlled", label: "Carb Controlled" },
  { value: "Other", label: "Other" },
];

export const NUTRITION_SECTION_TITLE = "Diagnosis & Nutrition";

const trim = (v) => (v == null ? "" : String(v).trim());

export const emptyNutrition = () => ({
  dietType: "",
  dietTypeOther: "",
  foodTexture: "",
  fluidManagement: "",
  mealAssistance: "",
  supplementEnteral: "",
  supplementOther: "",
  dietInstructions: "",
  sodiumRestriction: "",
  sodiumRestrictionDetail: "",
  fluidRestriction: "",
  carbohydrateRestriction: "",
  carbohydrateRestrictionDetail: "",
  restrictedFoods: "",
  textureRestrictions: "",
  restrictionNotes: "",
});

/** Normalizes patient.nutrition from API into edit form shape. */
export const buildNutrition = (patient = {}) => {
  const n = patient.nutrition;
  if (!n || typeof n !== "object") return emptyNutrition();
  return {
    dietType: n.dietType || "",
    dietTypeOther: n.dietTypeOther || "",
    foodTexture: n.foodTexture || "",
    fluidManagement: n.fluidManagement || "",
    mealAssistance: n.mealAssistance || "",
    supplementEnteral: n.supplementEnteral || "",
    supplementOther: n.supplementOther || "",
    dietInstructions: n.dietInstructions || "",
    sodiumRestriction: n.sodiumRestriction || "",
    sodiumRestrictionDetail: n.sodiumRestrictionDetail || "",
    fluidRestriction: n.fluidRestriction || "",
    carbohydrateRestriction: n.carbohydrateRestriction || "",
    carbohydrateRestrictionDetail: n.carbohydrateRestrictionDetail || "",
    restrictedFoods: n.restrictedFoods || "",
    textureRestrictions: n.textureRestrictions || "",
    restrictionNotes: n.restrictionNotes || "",
  };
};

const nullable = (value) => {
  const s = trim(value);
  return s || null;
};

/** API payload — nulls for empty strings. */
export const serializeNutrition = (nutrition = {}) => ({
  dietType: nullable(nutrition.dietType),
  dietTypeOther: nullable(nutrition.dietTypeOther),
  foodTexture: nullable(nutrition.foodTexture),
  fluidManagement: nullable(nutrition.fluidManagement),
  mealAssistance: nullable(nutrition.mealAssistance),
  supplementEnteral: nullable(nutrition.supplementEnteral),
  supplementOther: nullable(nutrition.supplementOther),
  dietInstructions: nullable(nutrition.dietInstructions),
  sodiumRestriction: nullable(nutrition.sodiumRestriction),
  sodiumRestrictionDetail: nullable(nutrition.sodiumRestrictionDetail),
  fluidRestriction: nullable(nutrition.fluidRestriction),
  carbohydrateRestriction: nullable(nutrition.carbohydrateRestriction),
  carbohydrateRestrictionDetail: nullable(
    nutrition.carbohydrateRestrictionDetail,
  ),
  restrictedFoods: nullable(nutrition.restrictedFoods),
  textureRestrictions: nullable(nutrition.textureRestrictions),
  restrictionNotes: nullable(nutrition.restrictionNotes),
});

/** Accepts a patient `{ nutrition }` or a flat nutrition row from `buildNutrition`. */
const resolveNutritionRow = (source = {}) => {
  if (
    source &&
    typeof source === "object" &&
    ("dietType" in source ||
      "foodTexture" in source ||
      "sodiumRestriction" in source ||
      "dietInstructions" in source)
  ) {
    return { ...emptyNutrition(), ...source };
  }
  return buildNutrition(source);
};

export const hasNutritionData = (source = {}) =>
  Object.values(resolveNutritionRow(source)).some((v) => trim(v));

export const hasDietData = (source = {}) => {
  const n = resolveNutritionRow(source);
  return [
    n.dietType,
    n.dietTypeOther,
    n.foodTexture,
    n.fluidManagement,
    n.mealAssistance,
    n.supplementEnteral,
    n.supplementOther,
    n.dietInstructions,
  ].some((v) => trim(v));
};

export const hasRestrictionData = (source = {}) => {
  const n = resolveNutritionRow(source);
  return [
    n.sodiumRestriction,
    n.sodiumRestrictionDetail,
    n.fluidRestriction,
    n.carbohydrateRestriction,
    n.carbohydrateRestrictionDetail,
    n.restrictedFoods,
    n.textureRestrictions,
    n.restrictionNotes,
  ].some((v) => trim(v));
};

/** Display label for diet type (includes Other detail). */
export const formatDietType = (nutrition = {}) => {
  const n = buildNutrition({ nutrition });
  if (!trim(n.dietType)) return "";
  if (n.dietType === "Other" && trim(n.dietTypeOther)) {
    return n.dietTypeOther;
  }
  return n.dietType;
};

export const formatSupplementEnteral = (nutrition = {}) => {
  const n = buildNutrition({ nutrition });
  if (!trim(n.supplementEnteral) || n.supplementEnteral === "None") return "";
  if (n.supplementEnteral === "Other" && trim(n.supplementOther)) {
    return n.supplementOther;
  }
  return n.supplementEnteral;
};

export const formatSodiumRestriction = (nutrition = {}) => {
  const n = buildNutrition({ nutrition });
  if (!trim(n.sodiumRestriction) || n.sodiumRestriction === "None") return "";
  if (n.sodiumRestriction === "Other" && trim(n.sodiumRestrictionDetail)) {
    return n.sodiumRestrictionDetail;
  }
  if (trim(n.sodiumRestrictionDetail)) {
    return `${n.sodiumRestriction} — ${n.sodiumRestrictionDetail}`;
  }
  return n.sodiumRestriction;
};

export const formatCarbohydrateRestriction = (nutrition = {}) => {
  const n = buildNutrition({ nutrition });
  if (!trim(n.carbohydrateRestriction) || n.carbohydrateRestriction === "None") {
    return "";
  }
  if (
    n.carbohydrateRestriction === "Other" &&
    trim(n.carbohydrateRestrictionDetail)
  ) {
    return n.carbohydrateRestrictionDetail;
  }
  if (trim(n.carbohydrateRestrictionDetail)) {
    return `${n.carbohydrateRestriction} — ${n.carbohydrateRestrictionDetail}`;
  }
  return n.carbohydrateRestriction;
};

/** Section-level validation for demographics save. */
export const validateNutritionFields = (nutrition = {}) => {
  const n = buildNutrition({ nutrition });
  if (n.dietType === "Other" && !trim(n.dietTypeOther)) {
    return "Specify diet type when Other is selected";
  }
  if (n.supplementEnteral === "Other" && !trim(n.supplementOther)) {
    return "Specify supplement / enteral when Other is selected";
  }
  if (n.sodiumRestriction === "Other" && !trim(n.sodiumRestrictionDetail)) {
    return "Specify sodium restriction when Other is selected";
  }
  if (
    n.carbohydrateRestriction === "Other" &&
    !trim(n.carbohydrateRestrictionDetail)
  ) {
    return "Specify carbohydrate restriction when Other is selected";
  }
  return null;
};

export const updateNutritionField = (prev, key, value) => ({
  ...prev,
  [key]: value,
});
